import { useState } from "react";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { Card } from "../../ui/Card.js";
import { Button } from "../../ui/Button.js";
import { Badge } from "../../ui/Badge.js";

interface StemMixerProps {
  sectionId: string;
}

// Canonical Demucs stem order. The mixer renders these first (when present),
// then any other stem the separation produced, alphabetically — so the layout
// is stable regardless of `Object.keys` insertion order.
const CANONICAL_ORDER = ["drums", "bass", "vocals", "other"] as const;

const EMPTY_STEMS: Record<string, never> = {};

/** Orders a section's materialized stem names: canonical first, then the rest. */
function orderStems(names: string[]): string[] {
  const known = CANONICAL_ORDER.filter((s) => names.includes(s));
  const extra = names.filter((n) => !CANONICAL_ORDER.includes(n as never)).sort();
  return [...known, ...extra];
}

export function StemMixer({ sectionId }: StemMixerProps): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const section = useCompositionStore((s) =>
    s.audioState.sections.find((sec) => sec.id === sectionId),
  );
  const stems = section?.stems ?? EMPTY_STEMS;
  const stemNames = orderStems(Object.keys(stems));
  const hasStems = stemNames.length > 0;

  // `section.audio` carries the source asset Demucs separates. No audio bound
  // yet → the section was never generated, so separation has nothing to chew.
  const sourceHash = section?.audio?.assetHash;
  const [separating, setSeparating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onMute(stem: string, muted: boolean): void {
    const pid = useCompositionStore.getState().projectId;
    if (!pid) return;
    void useCompositionStore.getState().appendMechanicalEvent({
      type: muted ? "StemUnmuted" : "StemMuted",
      projectId: pid,
      payload: { sectionId, stem },
    });
  }

  function onVolume(stem: string, volume: number): void {
    const pid = useCompositionStore.getState().projectId;
    if (!pid) return;
    void useCompositionStore.getState().appendMechanicalEvent({
      type: "StemVolumeChanged",
      projectId: pid,
      payload: { sectionId, stem, volume },
    });
  }

  async function onSeparate(): Promise<void> {
    const pid = useCompositionStore.getState().projectId;
    if (!pid || !sourceHash) return;
    setSeparating(true);
    setError(null);
    try {
      // Reuses the existing `stem-separation` worker job (studio-worker
      // app.module.ts). Demucs writes the stem assets and emits
      // SectionStemsBound — which materializes `section.stems` and flips this
      // card from the empty state to one row per extracted stem.
      await useCompositionStore.getState().enqueueJob({
        projectId: pid,
        kind: "stem-separation",
        payload: { projectId: pid, sectionId, assetHash: sourceHash },
      });
    } catch (e) {
      setSeparating(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card aria-label={`Stem mixer ${sectionId}`} className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
          Mixer de stems
        </span>
        {hasStems ? (
          <Badge tone="active" aria-label={`${stemNames.length} stems`}>
            {stemNames.length} {stemNames.length === 1 ? "stem" : "stems"}
          </Badge>
        ) : null}
      </header>

      {hasStems ? (
        <ul className="flex flex-col" aria-label="Stem tracks">
          {stemNames.map((stem) => {
            const state = stems[stem];
            const muted = state?.muted ?? false;
            const volume = state?.volume ?? 1;
            const bound = state?.assetHash !== undefined;
            return (
              <li
                key={stem}
                className="flex items-center gap-3 border-t border-border py-2 first:border-t-0 first:pt-0"
              >
                <label
                  className="flex w-20 shrink-0 items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest2 text-paper"
                  htmlFor={`stem-${sectionId}-${stem}-vol`}
                >
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 shrink-0 ${
                      muted ? "bg-ink-dim" : bound ? "bg-moss" : "bg-border"
                    }`}
                  />
                  <span className="truncate">{stem}</span>
                </label>
                <button
                  type="button"
                  aria-label={`${muted ? "Unmute" : "Mute"} ${stem}`}
                  aria-pressed={muted}
                  className={`shrink-0 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 transition-colors disabled:opacity-40 ${
                    muted
                      ? "border-vinyl bg-vinyl text-paper"
                      : "border-border text-ink-muted hover:border-paper hover:text-paper"
                  }`}
                  disabled={!projectId}
                  onClick={() => {
                    onMute(stem, muted);
                  }}
                >
                  {muted ? "Mudo" : "Soar"}
                </button>
                <input
                  id={`stem-${sectionId}-${stem}-vol`}
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  defaultValue={volume}
                  aria-label={`${stem} volume`}
                  disabled={!projectId || muted}
                  onChange={(e) => {
                    onVolume(stem, Number(e.target.value));
                  }}
                  className="h-1 flex-1 cursor-pointer appearance-none rounded-none bg-tapeHi accent-butter disabled:cursor-not-allowed disabled:opacity-40"
                />
                <span className="w-9 shrink-0 text-right font-mono text-[10px] tabular-nums text-ink-dim">
                  {Math.round(volume * 100)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="flex flex-col items-start gap-3 border border-dashed border-border bg-asphalt/40 px-4 py-6">
          {/* Empty state: the section has never been through Demucs. Show the
              real reason + the one action that fixes it — never 4 inert
              sliders that pretend a mix exists. */}
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[11px] uppercase tracking-widest2 text-paper">
              Sem stems separados
            </span>
            <p className="max-w-md text-xs leading-relaxed text-ink-muted">
              {sourceHash
                ? "Separe o áudio desta seção em faixas (bateria, baixo, vocais…) para mixar cada uma de forma independente."
                : "Gere o áudio desta seção primeiro — a separação de stems precisa de uma faixa para trabalhar."}
            </p>
          </div>
          <Button
            variant="primary"
            aria-label="Separar stems"
            disabled={!projectId || !sourceHash || separating}
            onClick={() => {
              void onSeparate();
            }}
          >
            {separating ? "Separando…" : "Separar stems"}
          </Button>
          {error ? (
            <span role="alert" className="text-xs text-accent-danger">
              {error}
            </span>
          ) : null}
        </div>
      )}
    </Card>
  );
}
